// src/components/MonthlyPreloaderModal.jsx
import { useEffect, useRef, useState } from "react";

const STORAGE_KEY_PREFIX = "step0Modal_lastShown";
const TOTAL_DURATION = 20000; // 20 segundos

function shouldShowModal(iduser) {
  try {
    const key = `${STORAGE_KEY_PREFIX}:${iduser || "anon"}`;
    const lastShown = localStorage.getItem(key);
    if (!lastShown) return true;

    const last = new Date(lastShown);
    const now = new Date();

    return (
      last.getMonth() !== now.getMonth() ||
      last.getFullYear() !== now.getFullYear()
    );
  } catch {
    return false;
  }
}

function markAsShown(iduser) {
  try {
    const key = `${STORAGE_KEY_PREFIX}:${iduser || "anon"}`;
    localStorage.setItem(key, new Date().toISOString());
  } catch {}
}

export default function MonthlyPreloaderModal({ iduser, canSkip = false }) {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(1);
  const modalRef = useRef(null);
  const bsModalRef = useRef(null);
  const timeoutsRef = useRef([]);

  const closeModal = () => {
    // Limpa todos os timeouts pendentes
    timeoutsRef.current.forEach((t) => clearTimeout(t));
    timeoutsRef.current = [];

    if (bsModalRef.current) bsModalRef.current.hide();
    setVisible(false);
  };

  useEffect(() => {
    if (!shouldShowModal(iduser)) return;

    markAsShown(iduser);
    setVisible(true);

    if (modalRef.current && window.bootstrap?.Modal) {
      bsModalRef.current = window.bootstrap.Modal.getOrCreateInstance(
        modalRef.current,
        { backdrop: "static", keyboard: false }
      );
      bsModalRef.current.show();
    }

    const stepDuration = TOTAL_DURATION / 4;

    const t2 = setTimeout(() => setStep(2), stepDuration);
    const t3 = setTimeout(() => setStep(3), stepDuration * 2);
    const t4 = setTimeout(() => setStep(4), stepDuration * 3);
    const tClose = setTimeout(() => {
      closeModal();
    }, TOTAL_DURATION);

    timeoutsRef.current = [t2, t3, t4, tClose];

    return () => {
      timeoutsRef.current.forEach((t) => clearTimeout(t));
      timeoutsRef.current = [];
      if (bsModalRef.current) bsModalRef.current.hide();
    };
  }, [iduser]);

  const messages = [
    "Analyzing your data...",
    "Compiling your posts...",
    "Setting up your AI Assistant...",
    "Ready for takeoff...",
  ];

  return (
    <>
      <style>{`
        .preloader {
          display: flex;
          justify-content: center;
          gap: 0rem;
          flex-wrap: wrap;
          align-items: center;
          padding-top: 2rem;
        }
        .slideItem {
          flex: 0 0 0;
          max-width: 0;
          opacity: 0;
          overflow: hidden;
          transition: flex-basis 500ms cubic-bezier(.2,.9,.3,1),
            max-width 500ms cubic-bezier(.2,.9,.3,1),
            opacity 400ms ease,
            transform 400ms cubic-bezier(.2,.9,.3,1);
          transform: translateY(8px);
          display: flex;
          justify-content: center;
        }
        .slideItem.show {
          flex: 0 0 18rem;
          max-width: 18rem;
          opacity: 1;
          transform: translateY(0);
        }
        @media (max-width: 768px) {
          .slideItem.show {
            flex: 0 0 40vw;
            max-width: 40vw;
          }
        }
        .slideItem img {
          width: 100%;
          height: auto;
          display: block;
          border-radius: 8px;
        }
        .preloader-skip-btn {
          position: absolute;
          top: 1.5rem;
          right: 1.5rem;
          z-index: 10;
        }
      `}</style>

      <div
        className="modal"
        id="step0Modal"
        ref={modalRef}
        tabIndex="-1"
        aria-labelledby="step0ModalLabel"
        aria-hidden={!visible}
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div className="modal-dialog modal-dialog-centered modal-fullscreen">
          <div
            className="modal-content rounded-0"
            style={{ background: "linear-gradient(0deg, #eddcff, #f6f5fc)" }}
          >
            <div className="modal-body p-4 position-relative">
              {canSkip && (
                <button
                  type="button"
                  className="btn btn-outline-primary preloader-skip-btn"
                  onClick={closeModal}
                >
                  Skip <i className="fi fi-rr-angle-right"></i>
                </button>
              )}

              <div className="row justify-content-center">
                <div className="col-md-10">
                  <div className="preloader mt-5 pt-5">
                    {[1, 2, 3, 4].map((n) => (
                      <div
                        key={n}
                        id={`l0${n}`}
                        className={`slideItem${step >= n ? " show" : ""}`}
                      >
                        <img
                          src={`assets/img/travelagentcollective/l0${n}.png`}
                          alt=""
                        />
                      </div>
                    ))}
                  </div>

                  {messages.map((msg, i) => {
                    const n = i + 1;
                    return (
                      <h6
                        key={n}
                        id={`t0${n}`}
                        className="text-primary text-center text-preloader animate__animated animate__fadeIn"
                        style={{ display: step === n ? "block" : "none" }}
                      >
                        {msg}
                      </h6>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
